import { useEffect } from "react";
import { Calendar, CheckCircle2, ArrowLeft, VideoOff } from "lucide-react";

import { ParceirosFooter } from "@/components/parceiros/ParceirosFooter";
import { SelecaoHeader } from "@/components/selecao/SelecaoHeader";
import { CTAAnchor } from "@/components/parceiros/cta";
import { useMetaPixel } from "@/hooks/useMetaPixel";
import { trackCompleteRegistration } from "@/lib/meta-pixel";
import {
  formatSessionDisplay,
  getNextSession,
} from "@/lib/selecao-sessions";

function buildGoogleCalendarUrl(start: Date): string {
  const end = new Date(start.getTime() + 60 * 60_000);
  const pad = (n: number) => n.toString().padStart(2, "0");
  const fmt = (d: Date) =>
    d.getUTCFullYear() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    "00Z";

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: "Allu — Encontro Exclusivo (Programa de Parceiros)",
    dates: `${fmt(start)}/${fmt(end)}`,
    details:
      "Live de apresentação do programa de parceiros Allu. 60 minutos. Sem replay.",
    location: "Link enviado por e-mail e WhatsApp",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

const SelecaoObrigado = () => {
  useMetaPixel();

  const next = getNextSession();
  const display = formatSessionDisplay(next.date);

  useEffect(() => {
    document.title = "Vaga confirmada — Allu";

    const prevBodyBg = document.body.style.backgroundColor;
    const prevBodyFont = document.body.style.fontFamily;
    document.body.style.backgroundColor = "#FAF8F5";
    document.body.style.color = "#112711";
    document.body.style.fontFamily =
      '"Plus Jakarta Sans", Inter, system-ui, -apple-system, sans-serif';

    trackCompleteRegistration({
      content_name: "Masterclass Allu — Programa de Parceiros",
      content_category: "selecao",
      event_date: next.iso,
    });

    return () => {
      document.body.style.backgroundColor = prevBodyBg;
      document.body.style.color = "";
      document.body.style.fontFamily = prevBodyFont;
    };
  }, [next.iso]);

  // Link "Adicionar ao Google Calendar" gerado server-less.
  const gcalUrl = buildGoogleCalendarUrl(next.date);

  return (
    <div
      className="min-h-screen bg-[#FAF8F5] text-[#112711] flex flex-col"
      style={{
        fontFamily:
          '"Plus Jakarta Sans", Inter, system-ui, -apple-system, sans-serif',
      }}
    >
      <SelecaoHeader />

      <main className="flex-1 flex items-center">
        <div className="container-narrow py-16 sm:py-24 w-full">
          <div className="max-w-2xl mx-auto text-center">
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-[#D1F8E2] ring-8 ring-[#D1F8E2]/40">
              <CheckCircle2 className="w-8 h-8 text-[#112711]" />
            </span>

            <h1
              className="font-bold text-[#112711] text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight"
              style={{ letterSpacing: "-0.02em" }}
            >
              Você garantiu sua vaga
            </h1>

            <p className="mt-5 text-base sm:text-lg text-zinc-600 leading-relaxed max-w-xl mx-auto">
              Fique atento ao seu e-mail e ao seu WhatsApp para pegar o link da
              live no dia da apresentação. Te enviaremos um lembrete para
              ajudar a não se esquecer.
            </p>

            {/* Card da próxima sessão */}
            <div className="mt-10 mx-auto max-w-md rounded-2xl bg-white p-6 sm:p-7 ring-1 ring-zinc-200 text-left">
              <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-zinc-500">
                Próxima live · você está confirmado
              </div>
              <div className="mt-2 text-lg sm:text-xl font-semibold text-[#112711] tracking-tight">
                {display}
              </div>
              <div className="mt-1 text-sm text-zinc-600">
                60 minutos · entenda 100% como funciona · sem replay
              </div>

              <a
                href={gcalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-semibold text-sm bg-[#112711] text-white hover:bg-[#1F4A33] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#112711]"
              >
                <Calendar className="w-4 h-4" />
                Adicionar ao Google Calendar
              </a>
            </div>

            <div className="mt-12 inline-flex items-center gap-2 rounded-full bg-[#F3DE2C]/30 ring-1 ring-[#F3DE2C]/50 px-4 py-2 text-xs font-semibold text-[#112711]">
              <VideoOff className="w-3.5 h-3.5" />
              Sem replay. Só quem aparece, captura.
            </div>

            <div className="mt-10">
              <CTAAnchor href="/selecao" variant="outline" size="md">
                <ArrowLeft className="w-4 h-4" />
                Voltar para a página
              </CTAAnchor>
            </div>

            <p className="mt-12 text-xs text-zinc-500">
              Dúvidas? Responda o e-mail de confirmação ou escreva pra{" "}
              <a
                href="mailto:yago@allugator.com"
                className="text-[#112711] underline underline-offset-4 hover:text-[#1F4A33]"
              >
                yago@allugator.com
              </a>
              .
            </p>
          </div>
        </div>
      </main>

      <ParceirosFooter />
    </div>
  );
};

export default SelecaoObrigado;
